import Image from 'next/image'
import { FunctionComponent } from 'react'
import { useMount } from 'react-use'
import styled from 'styled-components'
import aiBadgeImageSrc from '@/assets/images/pptx/ai-badge.png'
import routes from '@/hooks/use-authorization/routes'
import { OurIntlShape } from '@/i18n/typing'
import PlatformImage from '@/utils/export-pptx/components/platform-image'
import { designLayoutSize, PptxObjectType } from '@/utils/export-pptx/constants'
import { PlatformKolListSlide } from '@/utils/export-pptx/types/slide'
import { getThumbnail } from '@/utils/get-thumbnails'

interface SlideProps {
  slide: PlatformKolListSlide
  onSlideReady?: (slide: PlatformKolListSlide) => void
  formatMessage: OurIntlShape['formatMessage']
}

// todo: 需要新增連結點擊數嗎？
const SlideComponent: FunctionComponent<SlideProps> = ({
  slide,
  onSlideReady,
  formatMessage,
}) => {
  useMount(() => {
    onSlideReady?.(slide)
  })

  return (
    <Wrapper data-pptx-object={PptxObjectType.Shape}>
      <TitleBlock>
        <Title data-pptx-object={PptxObjectType.Text}>
          {formatMessage({ id: 'general:pptx_title_platform_analysis' })}
        </Title>
        <TopBrandLine data-pptx-object={PptxObjectType.Shape} />
      </TitleBlock>
      <BottomBrandLine data-pptx-object={PptxObjectType.Shape} />
      <PlatformSummary data-pptx-object={PptxObjectType.Shape}>
        <Row>
          <Platform>
            <PlatformImage
              height={54}
              platform={slide.payload.platform}
              width={54}
            />
            <span data-pptx-object={PptxObjectType.Text}>
              {formatMessage({ id: 'kol:side_nav_overview' })}
            </span>
          </Platform>
          {slide.payload.estimatedLikeCount && (
            <Statistic>
              <StatisticTitle data-pptx-object={PptxObjectType.Text}>
                {formatMessage({
                  id: 'general:column_title_estimated_like_count',
                })}
              </StatisticTitle>
              <StatisticValue data-pptx-object={PptxObjectType.Text}>
                {slide.payload.estimatedLikeCount}
              </StatisticValue>
            </Statistic>
          )}
          {slide.payload.estimatedCommentCount && (
            <Statistic>
              <StatisticTitle data-pptx-object={PptxObjectType.Text}>
                {formatMessage({
                  id: 'general:column_title_estimated_comment_count',
                })}
              </StatisticTitle>
              <StatisticValue data-pptx-object={PptxObjectType.Text}>
                {slide.payload.estimatedCommentCount}
              </StatisticValue>
            </Statistic>
          )}
          {slide.payload.estimatedShareCount && (
            <Statistic>
              <StatisticTitle data-pptx-object={PptxObjectType.Text}>
                {formatMessage({
                  id: 'general:column_title_estimated_share_count',
                })}
              </StatisticTitle>
              <StatisticValue data-pptx-object={PptxObjectType.Text}>
                {slide.payload.estimatedShareCount}
              </StatisticValue>
            </Statistic>
          )}
          {slide.payload.estimatedEngagementCount && (
            <Statistic>
              <StatisticTitle data-pptx-object={PptxObjectType.Text}>
                {formatMessage({
                  id: 'general:column_title_estimated_engagement_count',
                })}
              </StatisticTitle>
              <StatisticValue data-pptx-object={PptxObjectType.Text}>
                {slide.payload.estimatedEngagementCount}
              </StatisticValue>
            </Statistic>
          )}
          {slide.payload.estimatedEngagementRate && (
            <Statistic>
              <StatisticTitle data-pptx-object={PptxObjectType.Text}>
                {formatMessage({
                  id: 'general:column_title_estimated_engagement_rate',
                })}
              </StatisticTitle>
              <StatisticValue data-pptx-object={PptxObjectType.Text}>
                {slide.payload.estimatedEngagementRate}
              </StatisticValue>
            </Statistic>
          )}
          {slide.payload.estimatedViewCount && (
            <Statistic>
              <StatisticTitle data-pptx-object={PptxObjectType.Text}>
                {formatMessage({
                  id: 'general:column_title_estimated_view_count',
                })}
              </StatisticTitle>
              <StatisticValue data-pptx-object={PptxObjectType.Text}>
                {slide.payload.estimatedViewCount}
              </StatisticValue>
            </Statistic>
          )}
          {slide.payload.estimatedViewRate && (
            <Statistic>
              <StatisticTitle data-pptx-object={PptxObjectType.Text}>
                {formatMessage({
                  id: 'general:column_title_estimated_avg_view_rate',
                })}
              </StatisticTitle>
              <StatisticValue data-pptx-object={PptxObjectType.Text}>
                {slide.payload.estimatedViewRate}
              </StatisticValue>
            </Statistic>
          )}
          {slide.payload.estimatedTouchCount && (
            <Statistic>
              <StatisticTitle>
                <Image
                  data-original-size
                  alt='ai-badge'
                  data-pptx-object={PptxObjectType.Image}
                  height={20}
                  src={aiBadgeImageSrc}
                  width={42}
                />
                <span data-pptx-object={PptxObjectType.Text}>
                  {formatMessage({
                    id: 'general:column_title_estimated_touch_count',
                  })}
                </span>
              </StatisticTitle>
              <StatisticValue data-pptx-object={PptxObjectType.Text}>
                {slide.payload.estimatedTouchCount}
              </StatisticValue>
            </Statistic>
          )}
          {slide.payload.estimatedTouchRate && (
            <Statistic>
              <StatisticTitle>
                <Image
                  data-original-size
                  alt='ai-badge'
                  data-pptx-object={PptxObjectType.Image}
                  height={20}
                  src={aiBadgeImageSrc}
                  width={42}
                />
                <span data-pptx-object={PptxObjectType.Text}>
                  {formatMessage({
                    id: 'general:column_title_estimated_touch_rate',
                  })}
                </span>
              </StatisticTitle>
              <StatisticValue data-pptx-object={PptxObjectType.Text}>
                {slide.payload.estimatedTouchRate}
              </StatisticValue>
            </Statistic>
          )}
          {slide.payload.estimatedClickCount && (
            <Statistic>
              <StatisticTitle data-pptx-object={PptxObjectType.Text}>
                {formatMessage({
                  id: 'general:column_title_estimated_click_count',
                })}
              </StatisticTitle>
              <StatisticValue data-pptx-object={PptxObjectType.Text}>
                {slide.payload.estimatedClickCount}
              </StatisticValue>
            </Statistic>
          )}
        </Row>
      </PlatformSummary>
      <TableWrapper>
        <Tr isOdd data-pptx-object={PptxObjectType.Shape}>
          <Th data-pptx-object={PptxObjectType.Text}>
            {formatMessage({ id: 'general:kol_name' })}
          </Th>
          {slide.payload.estimatedLikeCount && (
            <Th data-pptx-object={PptxObjectType.Text}>
              {formatMessage({
                id: 'general:column_title_estimated_like_count',
              })}
            </Th>
          )}
          {slide.payload.estimatedCommentCount && (
            <Th data-pptx-object={PptxObjectType.Text}>
              {formatMessage({
                id: 'general:column_title_estimated_comment_count',
              })}
            </Th>
          )}
          {slide.payload.estimatedShareCount && (
            <Th data-pptx-object={PptxObjectType.Text}>
              {formatMessage({
                id: 'general:column_title_estimated_share_count',
              })}
            </Th>
          )}
          {slide.payload.estimatedEngagementCount && (
            <Th data-pptx-object={PptxObjectType.Text}>
              {formatMessage({
                id: 'general:column_title_estimated_engagement_count',
              })}
            </Th>
          )}
          {slide.payload.estimatedEngagementRate && (
            <Th data-pptx-object={PptxObjectType.Text}>
              {formatMessage({
                id: 'general:column_title_estimated_engagement_rate',
              })}
            </Th>
          )}
          {slide.payload.estimatedViewCount && (
            <Th data-pptx-object={PptxObjectType.Text}>
              {formatMessage({
                id: 'general:column_title_estimated_view_count',
              })}
            </Th>
          )}
          {slide.payload.estimatedViewRate && (
            <Th data-pptx-object={PptxObjectType.Text}>
              {formatMessage({
                id: 'general:column_title_estimated_avg_view_rate',
              })}
            </Th>
          )}
          {slide.payload.estimatedTouchCount && (
            <Th>
              <Image
                data-original-size
                alt='ai-badge'
                data-pptx-object={PptxObjectType.Image}
                height={20}
                src={aiBadgeImageSrc}
                width={42}
              />
              <span data-pptx-object={PptxObjectType.Text}>
                {formatMessage({
                  id: 'general:column_title_estimated_touch_count',
                })}
              </span>
            </Th>
          )}
          {slide.payload.estimatedTouchRate && (
            <Th>
              <Image
                data-original-size
                alt='ai-badge'
                data-pptx-object={PptxObjectType.Image}
                height={20}
                src={aiBadgeImageSrc}
                width={42}
              />
              <span data-pptx-object={PptxObjectType.Text}>
                {formatMessage({
                  id: 'general:column_title_estimated_touch_rate',
                })}
              </span>
            </Th>
          )}
          {slide.payload.estimatedClickCount && (
            <Th data-pptx-object={PptxObjectType.Text}>
              {formatMessage({
                id: 'general:column_title_estimated_click_count',
              })}
            </Th>
          )}
        </Tr>
        {slide.payload.kol_list.map((kol, index) => (
          <Tr isOdd={index % 2 === 0} key={kol.uuid + index}>
            <Td>
              <Image
                priority
                unoptimized
                alt='kol thumbnail'
                data-link={routes.kolId.stringifyUrl({
                  kolId: kol.uuid,
                })}
                data-pptx-object={PptxObjectType.Image}
                height={40}
                src={getThumbnail(kol.uuid)}
                style={{ borderRadius: '50%' }}
                width={40}
              />
              <span data-pptx-object={PptxObjectType.Text}>{kol.name}</span>
            </Td>
            {kol.estimatedLikeCount && (
              <Td data-pptx-object={PptxObjectType.Text}>
                {kol.estimatedLikeCount}
              </Td>
            )}
            {kol.estimatedCommentCount && (
              <Td data-pptx-object={PptxObjectType.Text}>
                {kol.estimatedCommentCount}
              </Td>
            )}
            {kol.estimatedShareCount && (
              <Td data-pptx-object={PptxObjectType.Text}>
                {kol.estimatedShareCount}
              </Td>
            )}
            {kol.estimatedEngagementCount && (
              <Td data-pptx-object={PptxObjectType.Text}>
                {kol.estimatedEngagementCount}
              </Td>
            )}
            {kol.estimatedEngagementRate && (
              <Td data-pptx-object={PptxObjectType.Text}>
                {kol.estimatedEngagementRate}
              </Td>
            )}
            {kol.estimatedViewCount && (
              <Td data-pptx-object={PptxObjectType.Text}>
                {kol.estimatedViewCount}
              </Td>
            )}
            {kol.estimatedViewRate && (
              <Td data-pptx-object={PptxObjectType.Text}>
                {kol.estimatedViewRate}
              </Td>
            )}
            {kol.estimatedTouchCount && (
              <Td data-pptx-object={PptxObjectType.Text}>
                {kol.estimatedTouchCount}
              </Td>
            )}
            {kol.estimatedTouchRate && (
              <Td data-pptx-object={PptxObjectType.Text}>
                {kol.estimatedTouchRate}
              </Td>
            )}
            {kol.estimatedClickCount && (
              <Td data-pptx-object={PptxObjectType.Text}>
                {kol.estimatedClickCount}
              </Td>
            )}
          </Tr>
        ))}
      </TableWrapper>
    </Wrapper>
  )
}

const BrandLine = styled.div`
  position: absolute;
  background: ${({ theme }): string => theme.colors.brand.primary};
  height: 1px;
`

const TopBrandLine = styled(BrandLine)`
  position: static;
  top: 72px;
  left: 358px;
  right: 64px;
`

const BottomBrandLine = styled(BrandLine)`
  bottom: 80px;
  left: 64px;
  right: 64px;
`

const Title = styled.div`
  color: ${({ theme }): string => theme.colors.text.primary};
  font-size: 42px;
  font-weight: 700;
  line-height: 70px;
`

const TitleBlock = styled.div`
  position: absolute;
  top: 37px;
  left: 64px;
  right: 64px;
  display: flex;
  gap: 24px;

  ${Title} {
    flex: 0 0 auto;
  }

  ${TopBrandLine} {
    margin-top: 34px;
    flex: 1 1 auto;
  }
`

const Wrapper = styled.div`
  font-family: Arial;
  width: ${designLayoutSize.w}px;
  height: ${designLayoutSize.h}px;
  position: relative;
  color: ${({ theme }): string => theme.colors.text.primary};
  background-color: ${({ theme }): string => theme.colors.border.divider};

  &,
  * {
    box-sizing: border-box;
  }
`

const StatisticTitle = styled.div`
  display: flex;
  gap: 8px;
  color: ${({ theme }): string => theme.colors.text.secondary};
  font-size: 13px;
  line-height: 18px;
`

const StatisticValue = styled.div`
  color: ${({ theme }): string => theme.colors.text.primary};
  font-size: 20px;
  font-weight: 700;
  line-height: 28px;
`

const Statistic = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const Platform = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  font-size: 20px;
  font-weight: 700;
  line-height: 32px;

  > span {
    flex: 1 1 auto;
    width: 1px;
  }
`

const PlatformSummary = styled.div`
  position: absolute;
  top: 132px;
  left: 64px;
  right: 64px;
  padding: 32px;
  background: ${({ theme }): string => theme.colors.background.white};
  border-radius: 16px;
`

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  word-break: break-all;

  & > * {
    width: 100px;
    flex: 1 1 auto;
  }
`
const Tr = styled(Row)<{ isOdd?: boolean }>`
  padding: 8px 32px;
  min-height: 68px;
  background: ${({ isOdd, theme }): string =>
    isOdd ? theme.colors.background.grey : 'transparent'};
`

const Th = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  font-size: 14px;
  font-weight: 700;
  line-height: 19px;
`
const Td = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  font-size: 14px;
  line-height: 19px;
`

const TableWrapper = styled.div`
  position: absolute;
  top: 275px;
  left: 64px;
  right: 64px;
`

export default SlideComponent
